import {
  IItemCacheDispatch,
  IItemCacheThunkAction
} from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { IItemCache } from '@ms/items-view/lib/private/models/IItemCacheObserver';
import { ApiError } from '@ms/utilities-error/lib/index';
import { readItem } from '@ms/items-view/lib/private/operations/readItem';
import {
  spDataSourceConfigurationFacet,
  spPageContextForDataRequestorFacet,
  enableUserAgentConfigurationFacet,
  spPrefetchDataConfigurationFacet
} from './Facets';
import {
  DataRequestor,
  IDataRequestor,
  IErrorData,
  IDataRequestorPageContext
} from '@ms/odsp-datasources/lib/dataSources/base/DataRequestor';
import {
  GetDataError,
  IGetDataErrorProps
} from '@ms/odsp-datasources/lib/dataSources/base/GetDataError';
import { getJSONResponse, setJSONResponse, isOffline } from '@ms/items-view/lib/private/operations/offlineCache';
import { logQoS } from '@ms/items-view/lib/private/operations/logQoS';
import SPOErrorStrings from './SPOErrors.resx';
import { spPageContextForDataRequestorKeyFacet } from './Keys';
import { IGetDataOptions } from './interfaces/IGetServerData';
import { readItems } from '@ms/items-view/lib/private/operations/readItems';
import { configurationItemKey } from '@ms/items-view/lib/private/facets/ConfigurationFacets';
import { readConfiguration } from '@ms/items-view/lib/private/operations/configuration/readConfiguration';
import { getTelemetryManager } from '@ms/items-view/lib/private/operations/telemetry/getTelemetryManager';
import { ResultTypeEnum } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import { Api } from '@ms/odsp-utilities/lib/logging/events/Api.event';
import { handleError } from '@ms/items-view/lib/private/operations/handleError';
import { getQosEndSchemaFromError } from '@ms/telemetry-manager/lib/utilities/getQosEndSchemaFromError';
import { mergeQosEndSchema } from '@ms/telemetry-manager/lib/utilities/mergeQosEndSchema';

export function getDataRequestor(payload: {
  webAbsoluteUrl: string;
  listFullUrl?: string;
}): IItemCacheThunkAction<{
  dataRequestor: IDataRequestor;
  hasDigest: boolean;
}> {
  return (dispatch: IItemCacheDispatch) => {
    const manager = dispatch(getTelemetryManager);

    let pageContext: IDataRequestorPageContext | undefined = undefined;

    const spPageContextFacets = dispatch(readItem({
      pageContext: spPageContextForDataRequestorFacet
    }, spPageContextForDataRequestorKeyFacet.serialize({
      webAbsoluteUrl: payload.webAbsoluteUrl
    })));

    if (spPageContextFacets && spPageContextFacets.pageContext) {
      pageContext = spPageContextFacets.pageContext;
    }

    const dataRequestor = new DataRequestor({
      normalizeErrors: true
    }, {
        pageContext: pageContext,
        apiEvent: Api.withManager(manager)
      });

    return {
      dataRequestor,
      hasDigest: !!pageContext
    };
  };
}

export { GetDataError, IGetDataErrorProps };

export function getAuthToken(payload: {
  url: string;
  disallowCookies?: boolean;
}): IItemCacheThunkAction<Promise<string | undefined>> {
  const {
    url,
    disallowCookies
  } = payload;

  return async (dispatch: IItemCacheDispatch) => {
    const {
      spDataSourceConfiguration
    } = dispatch(readConfiguration({
      spDataSourceConfiguration: spDataSourceConfigurationFacet
    }));

    if (!spDataSourceConfiguration) {
      dispatch(handleError({
        error: new Error(
          `There is no '${spDataSourceConfigurationFacet.debugName}' facet specified for the SharePoint Addon. ` +
          `This means that all requests will attempt to use cookies, and may fail unless a page context containing ` +
          `request digest information is resolved into the ItemCache.`
        )
      }));
    }

    const ensureAuthToken = spDataSourceConfiguration && spDataSourceConfiguration.ensureAuthToken;
    const ensureAuthTokenWithAction = spDataSourceConfiguration && spDataSourceConfiguration.ensureAuthTokenWithAction;

    let authToken: string | undefined;

    const fetchAuthToken = ensureAuthTokenWithAction ? async () => {
      // prefer the auth token action, but fall back to the older method if newer method is not implemented
      return await dispatch(ensureAuthTokenWithAction(url, { disallowCookies }));
    } : ensureAuthToken ? async () => {
      return await ensureAuthToken(url, { disallowCookies });
    } : undefined;

    if (fetchAuthToken) {
      const endQos = dispatch(logQoS({
        name: 'spDataSource.getAuthToken'
      }));

      try {
        authToken = await fetchAuthToken();

        dispatch(endQos({
          resultType: ResultTypeEnum.Success
        }));
      } catch (e) {
        dispatch(endQos(mergeQosEndSchema(getQosEndSchemaFromError(e))));

        throw (e instanceof ApiError ? e : new ApiError({
          innerError: e,
          code: 'UnknownAuthError'
        }));
      }
    }

    return authToken;
  };
}

export function getData<TResponse = void>(payload: IGetDataOptions<TResponse>): IItemCacheThunkAction<Promise<TResponse>> {
  const {
    webAbsoluteUrl,
    listFullUrl,
    url,
    additionalHeaders,
    body,
    contentType,
    parseResponse,
    method,
    needsRequestDigest,
    crossSiteCollectionCall,
    skipAuth,
    qosHandler,
    successQosHandler
  } = payload;

  const qosName = 'getData.' + payload.qosName;

  return async (dispatch: IItemCacheDispatch) => {
    const {
      dataRequestor,
      hasDigest
    } = dispatch(getDataRequestor({
      webAbsoluteUrl: webAbsoluteUrl,
      listFullUrl: listFullUrl
    }));

    const authToken = skipAuth ? undefined : await dispatch(getAuthToken({
      url: webAbsoluteUrl || listFullUrl || url,
      disallowCookies: !hasDigest
    }));

    /**
     * If payload set noRedirect option, we use it.
     * Otherwise check the configuration, if configuration set it, we use it.
     * When both payload and configuration don't set noRedirect option, we set it to true.
     * We want to make sure in the default case, noRedirect is true since most host pages are no long only contain one list.
     * We should only allow redirect in odsp-next scenario when the host page only render one list/library.
     */
    let noRedirect = payload.noRedirect;

    const {
      spDataSourceConfiguration
    } = dispatch(readConfiguration({
      spDataSourceConfiguration: spDataSourceConfigurationFacet
    }));

    if (noRedirect === undefined) {
      // should only set noRedirect to false if dataRequestorAutoRedirect is true.
      noRedirect = !(spDataSourceConfiguration && spDataSourceConfiguration.dataRequestorAutoRedirect);
    }

    let headers = additionalHeaders;
    const isUserAgentEnabled = dispatch(readItems((itemCache: IItemCache) => {
      return itemCache.demandItemFacet(enableUserAgentConfigurationFacet, configurationItemKey);
    }));

    if (isUserAgentEnabled) {
      headers = {
        ...additionalHeaders,
        'user-agent': (window.navigator && window.navigator.userAgent)
      };
    }

    return dataRequestor.getData<TResponse>({
      url: url,
      additionalPostData: body,
      authToken,
      contentType,
      noRedirect,
      parseResponse,
      additionalHeaders: headers,
      method,
      qosName,
      needsRequestDigest,
      crossSiteCollectionCall,
      qosHandler,
      successQosHandler
    }).then((response: TResponse) => {
      return response;
    }).catch((error: Error | IErrorData) => {
      // DataRequestor typically throws non-standard errors unless there is a cancellation or a script error.
      if (!error || error instanceof Error) {
        if (error instanceof GetDataError) {
          return Promise.reject(error);
        }

        // If the error is well-formed, wrap it and rethrow it.
        return Promise.reject(new GetDataError({
          innerError: error
        }));
      }

      return Promise.reject(new GetDataError({
        errorData: error
      }));
    });
  };
}

/**
 * GetData function that does offline caching.
 * @param payload
 */
export function getDataWithCache<TResponse = void>(payload: IGetDataOptions<TResponse>): IItemCacheThunkAction<Promise<TResponse>> {
  const {
    listFullUrl,
    url,
    additionalHeaders,
    body,
    method = 'POST'
  } = payload;

  return async (dispatch: IItemCacheDispatch) => {
    const key = createJSONKey(listFullUrl || '', method, url, additionalHeaders, body);
    const isOfflineMode = await dispatch(isOffline());

    if (isOfflineMode) {
      const jsonResponse = await dispatch(getJSONResponse(key));
      if (jsonResponse) {
        return <TResponse>jsonResponse;
      } else {
        return Promise.reject(new GetDataError({
          innerError: new Error(SPOErrorStrings.errorInternetDisconnected)
        }));
      }
    }

    const response = await dispatch(getData<TResponse>(payload));
    const {
      prefetchDataConfig
    } = dispatch(readConfiguration({
      prefetchDataConfig: spPrefetchDataConfigurationFacet
    }));
    const listDataUpdateCallback = prefetchDataConfig && prefetchDataConfig.listDataUpdateCallback;
    if (listDataUpdateCallback) {
      listDataUpdateCallback(url, body, JSON.stringify(response));
    }

    // Store the response into offline cache
    await dispatch(setJSONResponse(key, response));
    return response;
  };
}

/**
 * Create offline cache key based on network request query
 * @param listFullUrl - The full url of the query
 * @param method - The http method used
 * @param url - The url of the query
 * @param additionalHeaders - Any additional header information in the query
 * @param body - The body of the query
 */
function createJSONKey(listFullUrl: string, method: string, url: string,
  additionalHeaders?: { [key: string]: string }, body?: string): string {
  const postData = typeof body !== undefined ? JSON.stringify(body) : '';
  let headers = typeof body !== undefined ? JSON.stringify(additionalHeaders) : '';
  const key = url + '__' + listFullUrl + '__' + method + '__' + headers + '__' + postData;
  return key;
}
